import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { getFileUploadName, truncateFileName, safeStringify } from "src/utils/common";
import { useTranslation } from "react-i18next";
import { JsonView, allExpanded, defaultStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import ModalViewMainTab from "./components/ModalViewMainTab";
import ModalViewVehicleTab from "./components/ModalViewVehicleTab";
import ModalViewEnvironment from "./components/ModalViewEnvironment";
import ModalViewInitialCondition from "./components/ModalViewInitialCondition";
import ModalViewTarget from "./components/ModalViewTarget";
import ReactDiffViewer from "react-diff-viewer";
import MCSSigmaParam from "./components/ModalViewMCSSigmaParam";
import MCSCriteria from "./components/ModalViewMCSCriteria";
import { SIMULATION_TYPE } from "src/constant/common";

const ModalSeriesFilesView6DoF = ({ isOpen, serieData, onClose }) => {
  const { t } = useTranslation("flight-simulator");
  const [toggle, setToggle] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [scrollPositionById, setScrollPositionById] = useState({ 1: 0, 2: 0 });
  const isMSimulationModeMCS = serieData?.type === SIMULATION_TYPE.GNC_6DoF_MCS;
  const closeModal = () => {
    onClose();
    setToggle(false);
    setTimeout(() => {
      document.activeElement.blur();
    }, 500);
  };

  const showJsonContent = (jsonObj) => {
    if (typeof jsonObj === "string" && jsonObj.trim() === "") {
      return <div />;
    }
    if (typeof jsonObj !== "object") {
      return <div>{t("file_error")}</div>;
    }
    return <JsonView data={jsonObj} shouldExpandNode={allExpanded} style={{ ...defaultStyles, container: "bg-white" }} />;
  };

  const getScrollPosition = () => {
    const div = document.getElementById("tabContentModal");
    return div.scrollTop;
  };

  const reorderKeysByReference = (inputDataContent = {}, referenceJsonContent = {}) => {
    const inputKeys = new Set(Object.keys(inputDataContent));
    const orderedObject = {};

    Object.keys(referenceJsonContent).forEach((key) => {
      if (!inputKeys.has(key)) {
        return;
      }

      const inputValue = inputDataContent[key];
      const referenceValue = referenceJsonContent[key];

      const isInputObject = typeof inputValue === "object" && !Array.isArray(inputValue);
      const isReferenceObject = typeof referenceValue === "object" && !Array.isArray(referenceValue);

      if (isInputObject && isReferenceObject) {
        orderedObject[key] = reorderKeysByReference(inputValue, referenceValue);
        return;
      }

      orderedObject[key] = inputValue;
    });

    Object.keys(inputDataContent).forEach((key) => {
      if (!referenceJsonContent.hasOwnProperty(key)) {
        orderedObject[key] = inputDataContent[key];
      }
    });

    return orderedObject;
  };

  useEffect(() => {
    const element = document.getElementById("tabContentModal");
    if (element) {
      element.scrollTop = scrollPositionById[activeTab];
    }
  }, [activeTab]);

  useEffect(() => {
    setToggle(isOpen);
    setActiveTab(1);
    setScrollPositionById({ 1: 0, 2: 0 });
  }, [isOpen]);

  return (
    <Modal centered isOpen={toggle} toggle={closeModal} className="modal-full-width" backdrop="static">
      <ModalHeader className="p-b-0" toggle={closeModal}></ModalHeader>
      <ModalBody>
        <Nav tabs className="border-0">
          {serieData && (
            <>
              <NavItem className={`${activeTab === 1 ? "active" : ""}`}>
                <NavLink
                  role="button"
                  onClick={() => {
                    setScrollPositionById({
                      ...scrollPositionById,
                      [activeTab]: getScrollPosition(),
                    });
                    setActiveTab(1);
                  }}
                >
                  Main
                </NavLink>
              </NavItem>
              <NavItem className={`${activeTab === 2 ? "active" : ""}`}>
                <NavLink
                  role="button"
                  onClick={() => {
                    setScrollPositionById({
                      ...scrollPositionById,
                      [activeTab]: getScrollPosition(),
                    });
                    setActiveTab(2);
                  }}
                >
                  Vehicle
                </NavLink>
              </NavItem>
              <NavItem className={`${activeTab === 3 ? "active" : ""}`}>
                <NavLink
                  role="button"
                  onClick={() => {
                    setScrollPositionById({
                      ...scrollPositionById,
                      [activeTab]: getScrollPosition(),
                    });
                    setActiveTab(3);
                  }}
                >
                  Environment
                </NavLink>
              </NavItem>
              <NavItem className={`${activeTab === 4 ? "active" : ""}`}>
                <NavLink
                  role="button"
                  onClick={() => {
                    setScrollPositionById({
                      ...scrollPositionById,
                      [activeTab]: getScrollPosition(),
                    });
                    setActiveTab(4);
                  }}
                >
                  Initial Condition
                </NavLink>
              </NavItem>
              <NavItem className={`${activeTab === 5 ? "active" : ""}`}>
                <NavLink
                  role="button"
                  onClick={() => {
                    setScrollPositionById({
                      ...scrollPositionById,
                      [activeTab]: getScrollPosition(),
                    });
                    setActiveTab(5);
                  }}
                >
                  Target
                </NavLink>
              </NavItem>
              {isMSimulationModeMCS && (
                <>
                  <NavItem className={`${activeTab === 6 ? "active" : ""}`}>
                    <NavLink
                      role="button"
                      onClick={() => {
                        setScrollPositionById({
                          ...scrollPositionById,
                          [activeTab]: getScrollPosition(),
                        });
                        setActiveTab(6);
                      }}
                    >
                      MCS - Sigma Param
                    </NavLink>
                  </NavItem>
                  <NavItem className={`${activeTab === 7 ? "active" : ""}`}>
                    <NavLink
                      role="button"
                      onClick={() => {
                        setScrollPositionById({
                          ...scrollPositionById,
                          [activeTab]: getScrollPosition(),
                        });
                        setActiveTab(7);
                      }}
                    >
                      MCS Criteria
                    </NavLink>
                  </NavItem>
                </>
              )}
              <NavItem className={`${activeTab === 8 ? "active" : ""}`}>
                <NavLink
                  role="button"
                  onClick={() => {
                    setScrollPositionById({
                      ...scrollPositionById,
                      [activeTab]: getScrollPosition(),
                    });
                    setActiveTab(8);
                  }}
                >
                  {truncateFileName(getFileUploadName(serieData?.series?.parameter_path))}
                </NavLink>
              </NavItem>
              <NavItem className={`${activeTab === 9 ? "active" : ""}`}>
                <NavLink
                  role="button"
                  onClick={() => {
                    setScrollPositionById({
                      ...scrollPositionById,
                      [activeTab]: getScrollPosition(),
                    });
                    setActiveTab(9);
                  }}
                >
                  JSON Compare
                </NavLink>
              </NavItem>
            </>
          )}
        </Nav>
        <TabContent activeTab={activeTab} id="tabContentModal" className="h-full-screen">
          {serieData && (
            <>
              <TabPane tabId={1}>
                <ModalViewMainTab simulationData={serieData} />
              </TabPane>
              <TabPane tabId={2}>
                <ModalViewVehicleTab simulationData={serieData} />
              </TabPane>
              <TabPane tabId={3}>
                <ModalViewEnvironment simulationData={serieData} />
              </TabPane>
              <TabPane tabId={4}>
                <ModalViewInitialCondition simulationData={serieData} />
              </TabPane>
              <TabPane tabId={5}>
                <ModalViewTarget simulationData={serieData} />
              </TabPane>
              {isMSimulationModeMCS && (
                <>
                  <TabPane tabId={6}>
                    <MCSSigmaParam simulationData={serieData} />
                  </TabPane>
                  <TabPane tabId={7}>
                    <MCSCriteria simulationData={serieData} />
                  </TabPane>
                </>
              )}
              <TabPane tabId={8}>{showJsonContent(serieData.paramsJsonContent)}</TabPane>
              <TabPane tabId={9}>
                <ReactDiffViewer
                  className="mb-0 m-l-90"
                  oldValue={safeStringify(serieData.paramsJsonContent)}
                  newValue={safeStringify(reorderKeysByReference(serieData.input_data, serieData.paramsJsonContent))}
                  splitView
                  showDiffOnly
                />
              </TabPane>
            </>
          )}
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

export default ModalSeriesFilesView6DoF;
